import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Card, Row, Col, Avatar, Tag, Button, Space, Divider, Input, Select, Table, Modal, Descriptions, App } from 'antd';
import { 
  UserOutlined, 
  BookOutlined,
  SearchOutlined,
  FilterOutlined
} from '@ant-design/icons';
import api from '../services/api';
import { formatDateBeijing } from '../utils/datetime';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const ExpertDatabase = () => {
  const { message } = App.useApp();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    province: '',
    title: '',
    year: 2024,
    status: 'active',
  });
  const [viewMode, setViewMode] = useState('grid');
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const provinceOptions = [
    '北京', '天津', '河北', '山西', '内蒙古', '辽宁', '吉林', '黑龙江',
    '上海', '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南',
    '湖北', '湖南', '广东', '广西', '海南', '重庆', '四川', '贵州',
    '云南', '西藏', '陕西', '甘肃', '青海', '宁夏', '新疆'
  ];

  const titleOptions = [
    '教授级高级工程师', '正高级工程师', '高级工程师', '研究员', '教授', 
    '副教授', '副研究员', '教授级高级农艺师', '高级农艺师'
  ];

  const statusOptions = [
    { label: '在职', value: 'active' },
    { label: '不在库', value: 'inactive' },
    { label: '已排除', value: 'excluded' },
  ];

  const fetchExperts = useCallback(async (page = 1, pageSize = 12) => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        ...filters,
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await api.get('/experts', { params });
      setExperts(response.data.data);
      setPagination({
        current: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      });
    } catch (error) {
      message.error('获取专家列表失败');
      console.error('Error fetching experts:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchExperts(1, 12);
  }, [fetchExperts]);

  const handleSearch = (value) => {
    setFilters({ ...filters, search: value });
    fetchExperts(1, pagination.pageSize);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchExperts(1, pagination.pageSize);
  };

  const handleTableChange = (pagination) => {
    fetchExperts(pagination.current, pagination.pageSize);
  };

  const getStatusTag = (status) => {
    const config = {
      'active': { color: 'green', text: '在职' },
      'inactive': { color: 'default', text: '不在库' },
      'excluded': { color: 'red', text: '已排除' },
    };
    const statusConfig = config[status] || config['active'];
    return <Tag color={statusConfig.color}>{statusConfig.text}</Tag>;
  };

  const handleViewDetail = async (expert) => {
    setSelectedExpert(expert);
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const response = await api.get(`/experts/${expert.id}`);
      setSelectedExpert(response.data);
    } catch (error) {
      message.error('获取专家详情失败');
      console.error('Error fetching expert detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailClose = () => {
    setDetailVisible(false);
    setSelectedExpert(null);
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      fixed: 'left',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 60,
    },
    {
      title: '职称',
      dataIndex: 'title',
      key: 'title',
      width: 150,
    },
    {
      title: '省份',
      dataIndex: 'province',
      key: 'province',
      width: 80,
    },
    {
      title: '单位',
      dataIndex: 'organization',
      key: 'organization',
      width: 250,
      ellipsis: true,
    },
    {
      title: '入选年份',
      dataIndex: 'year',
      key: 'year',
      width: 100,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<BookOutlined />} onClick={() => handleViewDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>专家智库</Title>
        <Text type="secondary">专家咨询与经验分享平台</Text>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="搜索专家姓名"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="选择省份"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('province', value)}
              value={filters.province || undefined}
            >
              {provinceOptions.map(province => (
                <Option key={province} value={province}>{province}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="选择职称"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('title', value)}
              value={filters.title || undefined}
            >
              {titleOptions.map(title => (
                <Option key={title} value={title}>{title}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="选择年份"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('year', value)}
              value={filters.year || undefined}
            >
              {[2024, 2023, 2022, 2021].map(year => (
                <Option key={year} value={year}>{year}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder="选择状态"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('status', value)}
              value={filters.status || undefined}
            >
              {statusOptions.map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button 
                icon={<FilterOutlined />}
                onClick={() => setFilters({ search: '', province: '', title: '', year: 2024, status: 'active' })}
              >
                重置筛选
              </Button>
              <Button
                type={viewMode === 'grid' ? 'primary' : 'default'}
                onClick={() => setViewMode('grid')}
              >
                卡片
              </Button>
              <Button
                type={viewMode === 'table' ? 'primary' : 'default'}
                onClick={() => setViewMode('table')}
              >
                列表
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {viewMode === 'grid' ? (
        <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
          {experts.map((expert) => (
            <Col xs={24} sm={12} md={8} lg={6} key={expert.id}>
              <Card
                hoverable
                style={{ height: '100%' }}
                styles={{ body: { padding: '20px' } }}
              >
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <Avatar 
                    size={64} 
                    icon={<UserOutlined />} 
                    style={{ backgroundColor: '#1890ff', marginBottom: '12px' }}
                  />
                  <Title level={5} style={{ marginBottom: '4px' }}>{expert.name}</Title>
                  <Text type="secondary" style={{ fontSize: '13px' }}>{expert.title}</Text>
                  <div style={{ marginTop: '8px' }}>
                    {getStatusTag(expert.status)}
                  </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ marginBottom: '12px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>所属机构</Text>
                  <Text style={{ fontSize: '13px' }}>{expert.organization}</Text>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>省份</Text>
                    <Tag color="blue">{expert.province}</Tag>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>入选年份</Text>
                    <Text strong>{expert.year}</Text>
                  </div>
                </div>

                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Button type="primary" size="small" icon={<BookOutlined />} onClick={() => handleViewDetail(expert)}>
                    查看详情
                  </Button>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card>
          <Table
            columns={columns}
            dataSource={experts}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: handleTableChange,
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}

      {viewMode === 'grid' && (
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <Space>
            <Button 
              disabled={pagination.current === 1}
              onClick={() => fetchExperts(pagination.current - 1, pagination.pageSize)}
            >
              上一页
            </Button>
            <Text>
              第 {pagination.current} 页，共 {Math.ceil(pagination.total / pagination.pageSize)} 页
            </Text>
            <Button 
              disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
              onClick={() => fetchExperts(pagination.current + 1, pagination.pageSize)}
            >
              下一页
            </Button>
          </Space>
        </div>
      )}

      <Modal
        title="专家详情"
        open={detailVisible}
        onCancel={handleDetailClose}
        footer={[
          <Button key="close" onClick={handleDetailClose}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
        ) : selectedExpert ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Avatar 
                size={80} 
                icon={<UserOutlined />} 
                style={{ backgroundColor: '#1890ff', marginBottom: '12px' }}
              />
              <Title level={3} style={{ marginBottom: '8px' }}>{selectedExpert.name}</Title>
              <Text type="secondary">{selectedExpert.title}</Text>
              <div style={{ marginTop: '12px' }}>
                {getStatusTag(selectedExpert.status)}
              </div>
            </div>

            <Divider />

            <Descriptions column={2} bordered>
              <Descriptions.Item label="性别" span={1}>
                {selectedExpert.gender}
              </Descriptions.Item>
              <Descriptions.Item label="出生年月" span={1}>
                {selectedExpert.birth_year}年{selectedExpert.birth_month}月
              </Descriptions.Item>
              <Descriptions.Item label="所属机构" span={2}>
                {selectedExpert.organization}
              </Descriptions.Item>
              <Descriptions.Item label="省份" span={1}>
                <Tag color="blue">{selectedExpert.province}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="入选年份" span={1}>
                {selectedExpert.year}
              </Descriptions.Item>
              <Descriptions.Item label="是否退休" span={1}>
                {selectedExpert.is_retired ? '是' : '否'}
              </Descriptions.Item>
              <Descriptions.Item label="状态" span={1}>
                {getStatusTag(selectedExpert.status)}
              </Descriptions.Item>
              {selectedExpert.excluded_date && (
                <Descriptions.Item label="排除日期" span={2}>
                  {formatDateBeijing(selectedExpert.excluded_date)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default ExpertDatabase;